/**
 * How staff screens name a client: the name on top (profile name, else the CRM name, else the email)
 * and the CRM id underneath, so a row is told apart by who the client is rather than their address.
 * The email stays reachable on hover and in the client window.
 */
import type { ReactNode } from 'react';
import { cx } from './ui';

export interface ClientRef {
  name?: string | null;
  email?: string | null;
  crmId?: string | null;
}

/** "Maria Kovač", else the email, else "Client 48213". */
export const clientName = ({ name, email, crmId }: ClientRef) => name?.trim() || email || (crmId ? `Client ${crmId}` : '—');

/** One-line label for titles and prompts: "Maria Kovač · CRM 48213". */
export const clientLabel = (c: ClientRef) => (c.crmId ? `${clientName(c)} · CRM ${c.crmId}` : clientName(c));

/** Lower-cased haystack a client-side search box matches: name, email and CRM id. */
export const clientHaystack = ({ name, email, crmId }: ClientRef) => [name, email, crmId].filter(Boolean).join(' ').toLowerCase();

/** The Client cell: name, then "CRM 48213" in small mono (or a "No CRM ID" tag; none when the CRM id isn't known, as for a dealer). `onOpen` makes the name a button, `href` a link. */
export function ClientCell({ name, email, crmId, onOpen, href, className, children }: ClientRef & { onOpen?: () => void; href?: string; className?: string; children?: ReactNode }) {
  const label = clientName({ name, email, crmId });
  return (
    <span className={cx('flex min-w-0 flex-col gap-0.5 font-sans', className)} title={email ?? undefined}>
      {href ? (
        <a href={href} className="font-semibold text-fg hover:underline">
          {label}
        </a>
      ) : onOpen ? (
        <button type="button" className="text-left font-semibold text-fg hover:underline" onClick={(e) => (e.stopPropagation(), onOpen())}>
          {label}
        </button>
      ) : (
        <span className="font-semibold text-fg">{label}</span>
      )}
      {crmId ? (
        <span className="font-mono text-[11px] text-muted">CRM {crmId}</span>
      ) : crmId === undefined ? null : (
        <span className="self-start rounded bg-raised px-1.5 py-px text-[10px] font-medium text-muted">No CRM ID</span>
      )}
      {children}
    </span>
  );
}
